import React from 'react';
import { Check } from 'lucide-react';
import { getPageVisual, renderPageIcon } from '../../pageVisuals';

export interface PageSelectionModalProps {
  show: boolean;
  pageSelectionContext: {
    component: { name: string };
    newComponents?: any[];
  } | null;
  numPages: number | null;
  selectedPagesForCount: Set<number>;
  pageIndex: Array<{ page: number; label?: string; sheetName?: string }> | null | undefined;
  onTogglePage: (pg: number) => void;
  onCancel: () => void;
  onConfirm: () => void;
}

export const PageSelectionModal: React.FC<PageSelectionModalProps> = ({
  show,
  pageSelectionContext,
  numPages,
  selectedPagesForCount,
  pageIndex,
  onTogglePage,
  onCancel,
  onConfirm,
}) => {
  if (!show || !pageSelectionContext) return null;

  type BadgeColors = { bg: string; color: string; border: string };
  const getBadgeColors = (label?: string): BadgeColors | null => {
    const labelLower = label?.toLowerCase() || '';
    if (labelLower.includes('power')) return { bg: '#dcfce7', color: 'var(--green-text)', border: '#bbf7d0' };
    if (labelLower.includes('lighting') || labelLower.includes('reflected ceiling plan') || labelLower.includes('rcp')) {
      return { bg: '#fef9c3', color: 'var(--yellow-text)', border: '#fde68a' };
    }
    if (labelLower.includes('demolition') || labelLower.includes('demo')) return { bg: 'var(--red-tint-medium)', color: '#dc2626', border: '#fecaca' };
    if (labelLower.includes('fire alarm')) return { bg: '#ffedd5', color: 'var(--yellow-text-dark)', border: '#fed7aa' };
    if (labelLower.includes('low voltage') || labelLower.includes('telecom')) return { bg: '#ede9fe', color: 'var(--purple-text)', border: '#ddd6fe' };
    if (labelLower.includes('panel')) return { bg: '#cffafe', color: 'var(--cyan-text-dark)', border: '#a5f3fc' };
    if (label) return { bg: 'var(--bg-tertiary)', color: 'var(--text-secondary)', border: 'var(--border-color)' };
    return null;
  };

  const pageInfoByPage = new Map<number, { page: number; label?: string; sheetName?: string }>();
  (pageIndex || []).forEach((entry) => pageInfoByPage.set(entry.page, entry));

  const allPages = numPages ? Array.from({ length: numPages }, (_, i) => i + 1) : [];
  const selectedPages = allPages.filter((pg) => selectedPagesForCount.has(pg));
  const unselectedPages = allPages.filter((pg) => !selectedPagesForCount.has(pg));

  const renderPageCard = (pg: number) => {
    const selected = selectedPagesForCount.has(pg);
    const info = pageInfoByPage.get(pg);
    const badgeColors = getBadgeColors(info?.label);
    const visual = getPageVisual(info?.label);

    return (
      <button
        key={pg}
        type="button"
        onClick={() => onTogglePage(pg)}
        style={{
          padding: 0,
          border: selected ? '3px solid #3b82f6' : '2px solid var(--border-color)',
          borderRadius: '10px',
          overflow: 'hidden',
          background: 'var(--bg-secondary)',
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
        }}
      >
        <div
          style={{
            padding: '8px',
            background: 'var(--bg-secondary)',
          }}
        >
          <div
            style={{
              width: '100%',
              aspectRatio: '1.6 / 1',
              border: '1px solid var(--border-color)',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'var(--bg-secondary)',
            }}
          >
            {renderPageIcon(visual.type, visual.iconColor)}
          </div>
        </div>
        <span
          style={{
            padding: '6px',
            fontSize: '12px',
            fontWeight: 600,
            color: selected ? '#2563eb' : 'var(--text-secondary)',
            textAlign: 'center',
            background: 'var(--bg-primary)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '2px',
          }}
        >
          <span>
            Page {pg}
            {selected && <Check size={14} style={{ marginLeft: '4px', verticalAlign: 'middle' }} />}
          </span>
          {badgeColors && info?.label && (
            <span
              style={{
                fontSize: '10px',
                fontWeight: 600,
                color: badgeColors.color,
                background: badgeColors.bg,
                border: `1px solid ${badgeColors.border}`,
                borderRadius: '6px',
                padding: '1px 6px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '100%',
              }}
            >
              {info.sheetName ? `${info.sheetName} · ` : ''}{info.label}
            </span>
          )}
        </span>
      </button>
    );
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100010,
        padding: '20px',
        backdropFilter: 'blur(4px)'
      }}
      onClick={() => {}}
    >
      <div
        style={{
          background: 'var(--bg-primary)',
          borderRadius: '16px',
          padding: '24px',
          width: '95vw',
          maxWidth: '1280px',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 60px rgba(0, 0, 0, 0.3)',
          border: '1px solid var(--border-color)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ margin: 0, marginBottom: '4px', color: 'var(--text-primary)', fontSize: '20px', fontWeight: 600 }}>
          Page Selection
        </h3>
        <div style={{ flex: 1, marginBottom: '20px', minHeight: '260px', display: 'grid', gridTemplateRows: '3fr 1.45fr', gap: '12px' }}>
          <div style={{ minHeight: 0, display: 'flex', flexDirection: 'column', border: '1px solid var(--border-color)', borderRadius: '10px', padding: '10px' }}>
            <div style={{ marginBottom: '8px', fontSize: '12px', fontWeight: 700, color: 'var(--text-secondary)' }}>
              Default Selected Pages ({selectedPages.length})
            </div>
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
              {selectedPages.length > 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '8px' }}>
                  {selectedPages.map(renderPageCard)}
                </div>
              ) : (
                <div style={{ fontSize: '12px', color: 'var(--text-placeholder)', padding: '8px 4px' }}>
                  No pages selected yet.
                </div>
              )}
            </div>
          </div>

          <div style={{ minHeight: 0, display: 'flex', flexDirection: 'column', border: '1px solid var(--border-color)', borderRadius: '10px', padding: '10px' }}>
            <div style={{ marginBottom: '8px', fontSize: '12px', fontWeight: 700, color: 'var(--text-secondary)' }}>
              Click on boxes to select other pages ({unselectedPages.length})
            </div>
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
              {unselectedPages.length > 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '8px' }}>
                  {unselectedPages.map(renderPageCard)}
                </div>
              ) : (
                <div style={{ fontSize: '12px', color: 'var(--text-placeholder)', padding: '8px 4px' }}>
                  All pages are selected.
                </div>
              )}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', borderTop: '1px solid var(--border-color)', paddingTop: '16px' }}>
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: 500,
              color: 'var(--text-secondary)',
              background: 'var(--bg-tertiary)',
              border: '1px solid var(--border-strong)',
              borderRadius: '8px',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={selectedPagesForCount.size === 0}
            style={{
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: 600,
              color: '#ffffff',
              background: selectedPagesForCount.size === 0 ? 'var(--text-placeholder)' : '#3b82f6',
              border: 'none',
              borderRadius: '8px',
              cursor: selectedPagesForCount.size === 0 ? 'not-allowed' : 'pointer'
            }}
          >
            Start Counting ({selectedPagesForCount.size} {selectedPagesForCount.size === 1 ? 'page' : 'pages'})
          </button>
        </div>
      </div>
    </div>
  );
};
